import { useMutation, useQueryClient } from '@tanstack/react-query';
import { InterviewerService } from '../InterviewerService';

// Saves one question's rating + comment.
export const useSubmitQuestionResponse = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ interviewRoundDetailId, questionnaireId, payload }) =>
      InterviewerService.submitQuestionResponse(
        interviewRoundDetailId,
        questionnaireId,
        payload
      ),
    onSuccess: (_, { interviewRoundDetailId }) => {
      queryClient.invalidateQueries({
        queryKey: [
          'interviewer',
          'interview-questionnaire',
          interviewRoundDetailId,
        ],
      });
    },
  });
};

// Submits the overall scorecard (recommendation + summary comment) for an
// interview. The brief's `scorecards[]` list is refreshed on success since
// that's where this shows up. Also invalidates every `useInterviewerInterviews`
// list query (the Upcoming Interviews page's Upcoming/Scorecard due/Completed
// tabs, and their count badges) — a submitted verdict moves this interview
// out of "Scorecard due" and into "Completed", so without this the page the
// interviewer lands back on after submitting (see InterviewBrief.jsx's
// VerdictSuccessDialog → "Back to upcoming interviews") kept showing the
// stale pre-submit tab membership/counts. Partial key (no status/page/etc.)
// so this catches every tab's cached variant, not just whichever one was
// open when the brief was last visited.
export const useSubmitVerdict = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ interviewRoundDetailId, payload }) =>
      InterviewerService.submitVerdict(interviewRoundDetailId, payload),
    onSuccess: (_, { interviewRoundDetailId }) => {
      queryClient.invalidateQueries({
        queryKey: ['interviewer', 'interview-brief', interviewRoundDetailId],
      });
      queryClient.invalidateQueries({
        queryKey: ['interviewer', 'interviews'],
      });
    },
  });
};

// Saves the interviewer's sticky notes ({ comments: markdown }). Updates the
// notes query's cache directly with the just-saved payload rather than
// invalidating/refetching — the sticky-note widget is the only writer of
// this value, so there's nothing a refetch would reveal that the optimistic
// cache write doesn't already reflect, and skipping it avoids a flash/loader
// on every autosave tick.
export const useSaveInterviewNotes = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ interviewRoundDetailId, payload }) =>
      InterviewerService.saveNotes(interviewRoundDetailId, payload),
    onSuccess: (response, { interviewRoundDetailId }) => {
      queryClient.setQueryData(
        ['interviewer', 'interview-notes', interviewRoundDetailId],
        response
      );
    },
  });
};
